import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { flipmailAuthService } from "../services/flipmailAuth";
import { navigateAfterOAuthExchange } from "../utils/authNavigation";

const EXCHANGE_KEY = "flipmail_oauth_ex";
const EXCHANGE_LOCK_KEY = "flipmail_oauth_ex_lock";
const ERROR_KEY = "flipmail_oauth_err";

const ERROR_MESSAGES = {
  invalid_state: "Sign-in could not be verified. Try again.",
  token_exchange: "Could not finish sign-in with the provider. Try again.",
  profile_fetch: "Could not load your profile from the provider. Try again.",
  missing_email: "That account has no email on file. Use a different sign-in method.",
  beta_signup_restricted: "Sign-up is limited to approved beta emails right now.",
  oauth_not_configured: "Social sign-in is not configured. Use email and password or contact support.",
  account_exists_password:
    "An account with this email already uses password sign-in. Sign in with email and password, or contact support to connect Google.",
  provider_denied: "Sign-in was cancelled.",
};
export default function OAuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrlExchange = params.get("exchange");
    const fromUrlError = params.get("error");

    if (fromUrlExchange) {
      sessionStorage.setItem(EXCHANGE_KEY, fromUrlExchange);
    }
    if (fromUrlError) {
      sessionStorage.setItem(ERROR_KEY, fromUrlError);
    }

    const exchange = fromUrlExchange || sessionStorage.getItem(EXCHANGE_KEY);
    const errorCode = fromUrlError || sessionStorage.getItem(ERROR_KEY);

    window.history.replaceState({}, "", "/auth/callback/");

    if (errorCode) {
      sessionStorage.removeItem(ERROR_KEY);
      sessionStorage.removeItem(EXCHANGE_KEY);
      setErrorText(ERROR_MESSAGES[errorCode] || "Could not complete sign-in.");
      setStatus("error");
      return;
    }

    if (!exchange) {
      setErrorText("Missing sign-in token. Start again from the login page.");
      setStatus("error");
      return;
    }

    if (sessionStorage.getItem(EXCHANGE_LOCK_KEY) === exchange) {
      return;
    }
    sessionStorage.setItem(EXCHANGE_LOCK_KEY, exchange);

    flipmailAuthService
      .redeemOAuthExchange(exchange)
      .then((data) => {
        sessionStorage.removeItem(EXCHANGE_KEY);
        sessionStorage.removeItem(EXCHANGE_LOCK_KEY);
        if (data?.access && data?.refresh) {
          localStorage.setItem("accessToken", data.access);
          localStorage.setItem("refreshToken", data.refresh);
        }
        const onboardingCompleted = data?.onboarding_completed === true;
        navigateAfterOAuthExchange(navigate, onboardingCompleted);
      })
      .catch(() => {
        sessionStorage.removeItem(EXCHANGE_KEY);
        sessionStorage.removeItem(EXCHANGE_LOCK_KEY);
        setErrorText("Could not complete sign-in. Try again.");
        setStatus("error");
      });
  }, [navigate]);

  if (status === "error") {
    return (
      <div className="min-h-screen bg-theme-main flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <p className="font-mono text-sm text-rose-400 mb-6">{errorText}</p>
          <Link
            to="/login/"
            className="font-mono text-xs uppercase tracking-[0.1em] text-theme-muted hover:text-theme-accent"
          >
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-main flex items-center justify-center p-6">
      <p className="font-mono text-sm text-theme-dim">Signing you in…</p>
    </div>
  );
}
